import { PromiseActorLogic, fromPromise } from 'xstate';
import { APIFetch } from '../../../../lib/hooks/apiFetch';
import { getPaymentMethod } from '../../../../lib/handlers/get-payment-method';
import { getPaymentMethods } from '../../../../lib/handlers/get-payment-methods';
import { createPaymentMethod } from '../../../../lib/handlers/create-payment-method';
import {
  CreatePaymentMethodResult,
  EvaluateErrorResponseResult,
  FetchPaymentMethodResult,
  FetchPaymentMethodsResult,
} from './types/actors';
import {
  SendCreatePaymentMethodParams,
  SendFetchPaymentMethodParams,
  SendFetchPaymentMethodsParams,
} from './types/events';
import { getPaymentMethodError } from './utils';

type PaymentMethodFactoryDeps = {
  apiFetch: APIFetch;
};
/**
 * Factory for fetch payment method actor
 * @param deps PaymentMethodFactoryDeps
 * @returns actor that fetches a payment method
 */
export const createFetchPaymentMethod = (
  deps: PaymentMethodFactoryDeps
): PromiseActorLogic<FetchPaymentMethodResult, SendFetchPaymentMethodParams> =>
  fromPromise<FetchPaymentMethodResult, SendFetchPaymentMethodParams>(
    async ({ input }) => {
      const { buyerID, resolve, reject } = input;
      try {
        if (!buyerID) {
          throw new Error('Cannot fetch payment method without buyer ID.');
        }
        const paymentMethodRes = await getPaymentMethod(
          deps.apiFetch,
          input.paymentMethodID,
          buyerID
        );

        return { paymentMethodRes, resolve, reject };
      } catch (error) {
        throw Object.assign(new Error('Fetch payment method failed'), {
          error,
          buyerID,
          resolve,
          reject,
        });
      }
    }
  );

/**
 * Factory for fetch payment methods actor
 * @param deps PaymentMethodFactoryDeps
 * @returns actor that fetches payment methods for a buyer
 */
export const createFetchPaymentMethods = (
  deps: PaymentMethodFactoryDeps
): PromiseActorLogic<
  FetchPaymentMethodsResult,
  SendFetchPaymentMethodsParams
> =>
  fromPromise<FetchPaymentMethodsResult, SendFetchPaymentMethodsParams>(
    async ({ input }) => {
      const { buyerID, resolve, reject } = input;
      try {
        if (!buyerID) {
          throw new Error('Cannot fetch payment methods without buyer ID.');
        }
        const paymentMethodsRes = await getPaymentMethods(
          deps.apiFetch,
          buyerID
        );

        return { paymentMethodsRes, resolve, reject };
      } catch (error) {
        throw Object.assign(new Error('Fetch payment methods failed'), {
          error,
          buyerID,
          resolve,
          reject,
        });
      }
    }
  );

/**
 * Factory for create payment method actor
 * @param deps PaymentMethodFactoryDeps
 * @returns actor that creates a payment method
 */
export const createCreatePaymentMethod = (
  deps: PaymentMethodFactoryDeps
): PromiseActorLogic<
  CreatePaymentMethodResult,
  SendCreatePaymentMethodParams
> =>
  fromPromise<CreatePaymentMethodResult, SendCreatePaymentMethodParams>(
    async ({ input }) => {
      const { buyerID, resolve, reject } = input;
      try {
        if (!buyerID) {
          throw new Error('Cannot create payment method without buyer ID.');
        }
        const createPaymentMethodsRes = await createPaymentMethod({
          apiFetch: deps.apiFetch,
          buyerId: buyerID,
          ...input.options,
        });

        return { createPaymentMethodsRes, resolve, reject };
      } catch (error) {
        throw Object.assign(new Error('Create payment method failed'), {
          error,
          buyerID,
          resolve,
          reject,
        });
      }
    }
  );

/**
 * Evaluate error response actor that extracts reason from payment method related errors
 */
export const evaluateErrorResponse = fromPromise<
  EvaluateErrorResponseResult,
  unknown
>(async ({ input }) => {
  const paymentMethodError = getPaymentMethodError(
    input as
      | FetchPaymentMethodResult
      | CreatePaymentMethodResult
      | FetchPaymentMethodsResult
  );

  return { reason: paymentMethodError?.reason ?? 'Unknown' };
});
